import { useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Info, Users } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

async function downloadCsv(path, filename) {
  const token = localStorage.getItem("ceap_token");
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("falha");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminImportAlunos() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) { toast.error("Selecione um arquivo .csv"); return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/users", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Importção concluída: ${data.created} criados, ${data.updated} atualizados`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload");
    } finally { setLoading(false); }
  };

  return (
    <AdminLayout title="Importar alunos em lote">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-xl"><CardContent className="p-5 space-y-3">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Modelo padrão</h3>
          <p className="text-sm text-muted-foreground">Baixe o modelo CSV, preencha e faça upload aqui. O sistema cria CEAP IDs automáticos e aplica a senha definida (padrão <span className="font-mono-id">Ceap@2025</span> se vazia).</p>
          <Button onClick={() => downloadCsv("/admin/import/users/template", "ceap_modelo_alunos.csv")} data-testid="download-users-template" className="w-full">
            <Download className="w-4 h-4 mr-2" /> Baixar modelo CSV (alunos)
          </Button>
          <div className="rounded-lg border border-[hsl(var(--accent)/0.25)] bg-[hsl(var(--accent)/0.04)] p-3 mt-3">
            <p className="text-xs font-medium text-[hsl(var(--primary))] flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Colunas suportadas</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              <strong>Obrigatórias:</strong> full_name, email.<br />
              <strong>Recomendadas:</strong> cpf, phone, cargo, orgao, cidade, estado.<br />
              <strong>Categoria/Nível:</strong> profile (legislativo|licitacoes|ambos), role (aluno_validado|operador|comercial|admin_master), level (Aluno CEAP|Aluno Recorrente|Especialista CEAP|Embaixador CEAP).<br />
              <strong>Histórico:</strong> hours_accumulated, courses_completed, is_validated (true/false), password.
            </p>
            <p className="text-[11px] text-muted-foreground mt-2">O nível será recalculado automaticamente conforme as horas acumuladas (mantém o maior entre o informado e o calculado).</p>
          </div>
        </CardContent></Card>

        <Card className="rounded-xl"><CardContent className="p-5 space-y-3">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] flex items-center gap-2"><Upload className="w-4 h-4" /> Upload do CSV preenchido</h3>
          <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="users-csv-file" />
          {file && <p className="text-xs text-muted-foreground">{file.name} • {(file.size / 1024).toFixed(1)} KB</p>}
          <Button onClick={upload} disabled={!file || loading} className="w-full" data-testid="users-csv-upload">
            {loading ? "Importando…" : "Importar alunos"}
          </Button>
          {result && (
            <div className="mt-3 space-y-2" data-testid="users-import-result">
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {result.created} criados</Badge>
                <Badge variant="outline">{result.updated} atualizados</Badge>
                {result.errors.length > 0 && <Badge className="bg-rose-100 text-rose-800 border-rose-200"><AlertCircle className="w-3.5 h-3.5 mr-1" /> {result.errors.length} erros</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">{result.total_rows} linha(s) processadas</p>
              {result.errors.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3 max-h-40 overflow-y-auto">
                  <p className="text-xs font-medium text-rose-800 mb-1">Erros encontrados:</p>
                  <ul className="text-[11px] text-rose-700 space-y-0.5">
                    {result.errors.slice(0, 50).map((e, i) => <li key={i}>Linha {e.line}: {e.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent></Card>
      </div>
    </AdminLayout>
  );
}
